/** @jsx jsx */

import { Fragment, ReactNode } from 'react';
import { capitalize } from 'lodash';

import { Grid, Text } from '@zapier/design-system';
import { jsx } from '@emotion/core';

import Styles from './Styles';
import { prettyType } from '../utils';

import type { EventType } from '../types';

type Props = {
  event: EventType;
};

const ListTitle = ({ title }: { title: string }) => <Text>{title}:</Text>;

const CodeBlock = ({ content }: { content: string }) => (
  <code css={Styles.code}>{content}</code>
);

const PreCodeBlock = ({ content }: { content: string }) => (
  <pre css={Styles.preWrap}>
    <code css={Styles.code}>{content}</code>
  </pre>
);

const ListItem = ({
  title,
  content = 'N/A',
}: {
  title: string;
  content: string;
}) => (
  <li>
    <Grid alignItems="center" gap={5}>
      <ListTitle title={title} />
      <CodeBlock content={content} />
    </Grid>
  </li>
);

/**
 * Reusable details wrapper
 */
const DetailsContainer = ({
  children,
  title,
}: {
  title: string;
  children: ReactNode;
}) => (
  <Fragment>
    <Text tag="h4" type="Title">
      {title}
    </Text>
    <ul>
      <Grid alignItems="center" gap={10}>
        {children}
      </Grid>
    </ul>
  </Fragment>
);

/**
 * Renders content for the general details
 *
 * @param event
 * @returns ReactNode
 */
const GeneralBlock = ({ event }: Props) => {
  return (
    <DetailsContainer title="General">
      <Fragment>
        <ListItem content={event.id} title="ID" />
        <ListItem
          content={`${event.date} (${event.timesince} ago)`}
          title="Date"
        />
        <ListItem content={prettyType(event.object_type)} title="Type" />
        <ListItem content={event.object_action} title="Key" />
      </Fragment>
    </DetailsContainer>
  );
};

/**
 * Renders content for the error details
 *
 * @param event
 * @returns ReactNode
 */
const ErrorDetails = ({ event }: Props) => {
  return (
    <DetailsContainer title="Error Information">
      <Fragment>
        <ListItem
          content={event.full_message || event.message}
          title="Message"
        />
        <ListItem content={event.traceback} title="Traceback" />
      </Fragment>
    </DetailsContainer>
  );
};

/**
 * Renders content for the console details
 *
 * @param event
 * @returns ReactNode
 */
const ConsoleDetails = ({ event }: Props) => {
  return (
    <DetailsContainer title="Console Information">
      <Fragment>
        <ListTitle title="Log" />
        <PreCodeBlock content={event.full_message || event.message} />
      </Fragment>
    </DetailsContainer>
  );
};

/**
 * Renders content for the bundle details
 *
 * @param event
 * @returns ReactNode
 */
const BundleDetails = ({ event }: Props) => (
  <DetailsContainer title="Bundle Information">
    <Fragment>
      {['message', 'input', 'output'].map(key => (
        <Grid alignItems="center" gap={5} key={key}>
          <ListTitle title={capitalize(key)} />
          <PreCodeBlock content={event[key]} />
        </Grid>
      ))}
    </Fragment>
  </DetailsContainer>
);

/**
 * Renders content for the request details
 *
 * @param event
 * @returns ReactNode
 */
const RequestDetails = ({ event }: Props) => {
  return (
    <DetailsContainer title="Request Information">
      <Fragment>
        <ListItem content={event.request_id} title="ID" />
        <ListItem content={event.request_method} title="Method" />
        <ListItem
          content={event.request_url || event.request_path}
          title="URL/Path"
        />
        <ListItem content={event.request_headers} title="Headers" />
        <ListItem content={event.request_params} title="Params" />
        <ListItem content={event.request_data} title="Data" />
        <ListItem content={event.request_duration_ms} title=" Duration (ms)" />
      </Fragment>
    </DetailsContainer>
  );
};

/**
 * Renders content for the response details
 *
 * @param event
 * @returns ReactNode
 */
const ResponseDetails = ({ event }: Props) => (
  <DetailsContainer title="Response Information">
    <Fragment>
      <ListItem content={event.response_status_code} title="Status Code" />
      <ListItem content={event.response_headers} title="Headers" />
      <ListItem
        content={event.request_url || event.request_path}
        title="URL/Path"
      />
      <ListItem content={event.response_content} title="Content" />
    </Fragment>
  </DetailsContainer>
);

export {
  BundleDetails,
  ConsoleDetails,
  ErrorDetails,
  GeneralBlock,
  ResponseDetails,
  RequestDetails,
};
